import { Component, inject, signal } from "@angular/core";
import { ROUTES } from "../../../../constants/routes";
import { Router, RouterLink } from "@angular/router";
import { email, form, FormField, minLength, required, submit, validate } from "@angular/forms/signals";
import { AccountPageWrapper } from "../../components/account-page-wrapper/account-page-wrapper";
import { PasswordInput } from "../../../../components/password-input/password-input";
import { AccountService } from "../../../../services/account.service";
import { ClientResponse } from "../../../../models/api-response.model";
import { PASSWORD_STRENGTH_PATTERN, PHONE_PATTERN } from "../../../../models/account/register.model";

interface RegisterModel {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

@Component({
  selector: "app-register",
  imports: [FormField, RouterLink, AccountPageWrapper, PasswordInput],
  templateUrl: "./register.html",
  styleUrl: "./register.scss"
})
export class Register {
  readonly routes = ROUTES;
  private readonly _accountService = inject(AccountService);
  private readonly _router = inject(Router);

  readonly model = signal<RegisterModel>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false
  });

  /** Set once the user tries to submit, so the terms error only shows after an attempt. */
  readonly termsTouched = signal(false);

  toggleTerms(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.model.update((current) => ({ ...current, acceptTerms: checked }));
    this.termsTouched.set(true);
  }

  readonly registerForm = form(this.model, (f) => {
    required(f.firstName, { message: "First name is required." });
    required(f.email, { message: "Email is required." });
    email(f.email, { message: "Enter a valid email address." });

    validate(f.phone, ({ value }) => {
      const phone = value().trim();
      if (phone && !PHONE_PATTERN.test(phone)) {
        return { kind: "pattern", message: "Enter a valid phone number." };
      }
      return null;
    });

    required(f.password, { message: "Password is required." });
    minLength(f.password, 8, { message: "Password must be at least 8 characters." });
    validate(f.password, ({ value }) => {
      const password = value();
      if (password && !PASSWORD_STRENGTH_PATTERN.test(password)) {
        return {
          kind: "pattern",
          message:
            "Password must contain at least one lowercase letter, one uppercase letter, one digit, and one special character."
        };
      }
      return null;
    });

    required(f.confirmPassword, { message: "Please confirm your password." });
    validate(f.confirmPassword, () => {
      const { password, confirmPassword } = this.model();
      if (confirmPassword && password !== confirmPassword) {
        return { kind: "custom", message: "Passwords do not match." };
      }
      return null;
    });

    validate(f.acceptTerms, ({ value }) => {
      if (!value()) {
        return {
          kind: "custom",
          message: "Please accept the Terms of Service and Privacy Policy to continue."
        };
      }
      return null;
    });
  });

  async onSubmit(event: Event): Promise<void> {
    event.preventDefault();
    this.termsTouched.set(true);

    if (!this.model().acceptTerms) {
      return;
    }

    await submit(this.registerForm, async () => {
      const { firstName, lastName, email, phone, password, confirmPassword } = this.model();

      return new Promise<void>((resolve) => {
        this._accountService
          .register({
            firstName,
            lastName: lastName || undefined,
            email,
            phone: phone.trim() || undefined,
            password,
            confirmPassword
          })
          .subscribe((response: ClientResponse<void>) => {
            if (response.isSuccess) {
              void this._router.navigate([ROUTES.accountLogin.url]);
            }

            resolve();
          });
      });
    });
  }
}
